/**
 * The primitives every screen is built from.
 *
 * Deliberately small and owned here rather than pulled from a component
 * library wholesale: one library, one set of tokens, no second button.
 */

import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

// ── Button ──────────────────────────────────────────────────────────

const buttonStyles = cva(
  'inline-flex items-center justify-center gap-2 rounded-[10px] font-medium ' +
    'transition-[background-color,border-color,color,box-shadow,opacity] duration-150 ' +
    'disabled:pointer-events-none disabled:opacity-50 whitespace-nowrap select-none',
  {
    variants: {
      variant: {
        // The fill is the untouched corporate #FF6B35, not the darkened
        // --brand: this is the button the identity is recognised by. White on
        // it is 2.84:1, so the foreground is --on-brand-bright (a near-black
        // brown, 5.77:1 by day and 6.62:1 at night) and the hover lightens
        // rather than darkens, which is the only direction that keeps a dark
        // ink readable. The glow under it is its own orange.
        primary:
          'bg-[var(--brand-bright)] text-[var(--on-brand-bright)] font-semibold shadow-[var(--shadow-brand)] hover:bg-[var(--brand-bright-hover)]',
        secondary:
          'border border-[var(--border-strong)] bg-[var(--surface)] text-[var(--text)] hover:bg-[var(--surface-2)]',
        ghost: 'text-[var(--text)] hover:bg-[var(--surface-2)]',
        // Destructive and irreversible actions look different, always
        // (docs/08-uiux.md §1, principle 4). The foreground is a token, not
        // `text-white`: in dark mode --danger is a light rose, and white on it
        // measured 2.77:1.
        danger: 'bg-[var(--danger)] text-[var(--on-danger)] font-semibold hover:opacity-90',
        // Mixed towards the brand on hover rather than `brightness-95`, which
        // darkened an already-dark chip at night until the hover vanished.
        subtle:
          'bg-[var(--brand-subtle)] text-[var(--brand)] hover:bg-[color-mix(in_oklab,var(--brand-subtle)_82%,var(--brand))]',
      },
      size: {
        sm: 'h-8 px-3 text-[13px]',
        md: 'h-9 px-4 text-sm',
        lg: 'h-11 px-6 text-base',
        // The kiosk minimum. Never smaller on a tablet.
        kiosk: 'h-14 px-8 text-lg',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  loading?: boolean
  icon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, icon, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonStyles({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  ),
)
Button.displayName = 'Button'

// ── Brand ───────────────────────────────────────────────────────────

/**
 * The EREG mark: the ITSthe1 orange tile with the E cut out of it.
 *
 * Drawn rather than set as a styled letter, and the distinction is not
 * cosmetic. The corporate #FF6B35 is 2.84:1 against white, so a real "E" in
 * a real text node is a genuine WCAG 1.4.3 failure and axe fails the build
 * for it — correctly, because it cannot know the letter is a logo. As a path
 * it is what it always was: a mark, which carries no contrast requirement,
 * and which can therefore use the untouched brand colour.
 *
 * The same geometry is in public/favicon.svg and scripts/make_icons.py.
 */
export function BrandMark({ size = 28, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      className={cn('shrink-0', className)}
      role="img"
      aria-label="EREG"
    >
      <rect width="32" height="32" rx="7" fill="var(--brand-bright)" />
      <path d="M10 9h12v3.4h-8.3v3h7.4v3.3h-7.4v3.9H22V26H10z" fill="#fff" />
    </svg>
  )
}

// ── Input ───────────────────────────────────────────────────────────

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
}

/**
 * The look of every text control — Input here, and the hand-rolled selects
 * and textareas on the admin screens, which import this rather than copying
 * it. An inset well at night (--field sits below the card it is on), white by
 * day. Focus is drawn by the control itself: the edge turns brand and a soft
 * halo appears, in place of the page-wide outline, which floated two pixels
 * outside a rounded field and looked detached from it.
 */
export const fieldStyles =
  'rounded-[10px] border bg-[var(--field)] text-[var(--text)] ' +
  'placeholder:text-[var(--text-muted)] transition-[border-color,box-shadow] duration-150 ' +
  'focus-visible:outline-none focus:border-[var(--brand)] focus:shadow-[0_0_0_3px_var(--focus-halo)] ' +
  'disabled:cursor-not-allowed disabled:opacity-60'

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'h-9 w-full px-3 text-sm',
        fieldStyles,
        invalid ? 'border-[var(--danger)]' : 'border-[var(--border-strong)]',
        className,
      )}
      {...props}
    />
  ),
)
Input.displayName = 'Input'

export function Label({
  children,
  required,
  htmlFor,
  className,
}: {
  children: ReactNode
  required?: boolean
  htmlFor?: string
  className?: string
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('block text-[13px] font-medium text-[var(--text)]', className)}
    >
      {children}
      {required && (
        <span className="ms-1 text-[var(--danger)]" aria-label="required">
          *
        </span>
      )}
    </label>
  )
}

export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  children,
}: {
  label: string
  htmlFor?: string
  required?: boolean
  hint?: string
  error?: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor} required={required}>
        {label}
      </Label>
      {children}
      {error ? (
        <p className="text-[12px] text-[var(--danger)]">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-[var(--text-muted)]">{hint}</p>
      ) : null}
    </div>
  )
}

// ── Surfaces ────────────────────────────────────────────────────────

export function Card({
  children,
  className,
  as: Tag = 'div',
  variant = 'solid',
}: {
  children: ReactNode
  className?: string
  as?: 'div' | 'section' | 'article'
  /**
   * `solid` is the card everywhere: opaque, one step above the ground, a
   * hairline edge and (at night) a lit top edge — styles/tokens.css,
   * "Depth, light and glass". `panel` is the translucent variant that lets
   * the ground's light through. `glow` is a solid card lit from its top-left
   * corner, as the reference KPI tiles are.
   */
  variant?: 'solid' | 'panel' | 'glow'
}) {
  return (
    <Tag
      className={cn(
        variant === 'panel'
          ? 'panel rounded-[16px]'
          : 'rounded-[16px] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-card)]',
        variant === 'glow' && 'card-glow',
        className,
      )}
    >
      {children}
    </Tag>
  )
}

// ── Badges and status ───────────────────────────────────────────────

const badgeStyles = cva(
  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12px] font-medium',
  {
    variants: {
      tone: {
        neutral: 'bg-[var(--surface-2)] text-[var(--text-muted)]',
        brand: 'bg-[var(--brand-subtle)] text-[var(--brand)]',
        success: 'bg-[var(--success-subtle)] text-[var(--success)]',
        warning: 'bg-[var(--warning-subtle)] text-[var(--warning)]',
        danger: 'bg-[var(--danger-subtle)] text-[var(--danger)]',
        info: 'bg-[var(--info-subtle)] text-[var(--info)]',
      },
      outline: { true: 'bg-transparent ring-1 ring-inset', false: '' },
    },
    defaultVariants: { tone: 'neutral', outline: false },
  },
)

export function Badge({
  children,
  tone,
  outline,
  className,
}: VariantProps<typeof badgeStyles> & { children: ReactNode; className?: string }) {
  return <span className={cn(badgeStyles({ tone, outline }), className)}>{children}</span>
}

const DOT_TONES: Record<string, string> = {
  online: 'bg-[var(--success)]',
  idle: 'bg-[var(--success)]',
  in_session: 'bg-[var(--info)]',
  offline: 'bg-[var(--text-muted)]',
  unregistered: 'bg-[var(--warning)]',
  retired: 'bg-[var(--danger)]',
  open: 'bg-[var(--success)]',
  closed: 'bg-[var(--text-muted)]',
}

/**
 * A status dot always carries its label.
 *
 * Colour is never the sole carrier of meaning — someone with deuteranopia has
 * to be able to read the tablet status too.
 */
export function StatusDot({ status, label }: { status: string; label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] text-[var(--text-muted)]">
      <span
        className={cn('h-2 w-2 shrink-0 rounded-full', DOT_TONES[status] ?? DOT_TONES.offline)}
        aria-hidden
      />
      {label ?? status.replace(/_/g, ' ')}
    </span>
  )
}

// ── Feedback ────────────────────────────────────────────────────────

export function Spinner({ className }: { className?: string }) {
  return (
    <Loader2
      className={cn('h-4 w-4 animate-spin text-[var(--text-muted)]', className)}
      aria-label="Loading"
    />
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-4 w-full', className)} aria-hidden />
}

export function Separator({ className }: { className?: string }) {
  return <hr className={cn('border-0 border-t border-[var(--border)]', className)} />
}

export function Progress({ value, label }: { value: number; label?: string }) {
  const clamped = Math.max(0, Math.min(100, value))
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? 'Progress'}
    >
      <div
        className="h-full rounded-full bg-[var(--brand)] transition-[width] duration-300"
        style={{ width: `${clamped}%` }}
      />
    </div>
  )
}
